const EVENT_PROPERTIES = [
  "analyticsContext",
  "analyticsMethod",
  "analyticsPage",
  "analyticsPeriod",
  "analyticsSection",
  "analyticsTopic",
  "analyticsView",
]

const ACQUISITION_KEY = "vaibe_anonymous_acquisition"

function configuration() {
  const token = document.querySelector("meta[name='vaibe-analytics-token']")?.content
  const host = document.querySelector("meta[name='vaibe-analytics-host']")?.content
  if (!token || !host || privacySignalEnabled()) return null

  return { token, host: host.replace(/\/+$/, "") }
}

function privacySignalEnabled() {
  return navigator.globalPrivacyControl === true ||
    navigator.doNotTrack === "1" ||
    window.doNotTrack === "1"
}

function sessionIdentifier() {
  const key = "vaibe_anonymous_activity_id"

  try {
    const existing = window.sessionStorage.getItem(key)
    if (existing) return existing

    const generated = window.crypto.randomUUID()
    window.sessionStorage.setItem(key, generated)
    return generated
  } catch (_error) {
    return window.crypto.randomUUID()
  }
}

function pageContext() {
  const page = document.body?.dataset.analyticsPage
  if (!page) return null

  return compact({
    page,
    period: document.body.dataset.analyticsPeriod,
    topic: document.body.dataset.analyticsTopic,
  })
}

function acquisitionProperties() {
  try {
    const existing = window.sessionStorage.getItem(ACQUISITION_KEY)
    if (existing) return JSON.parse(existing)

    const created = buildAcquisitionProperties()
    window.sessionStorage.setItem(ACQUISITION_KEY, JSON.stringify(created))
    return created
  } catch (_error) {
    return buildAcquisitionProperties()
  }
}

function buildAcquisitionProperties() {
  const query = new URLSearchParams(window.location.search)

  return compact({
    referring_domain: referringDomain(),
    utm_source: campaignValue(query.get("utm_source")),
    utm_medium: campaignValue(query.get("utm_medium")),
    utm_campaign: campaignValue(query.get("utm_campaign")),
  })
}

function referringDomain() {
  if (!document.referrer) return "direct"

  try {
    const domain = new URL(document.referrer).hostname.replace(/^www\./, "")
    const currentDomain = window.location.hostname.replace(/^www\./, "")
    return domain === currentDomain ? "direct" : domain
  } catch (_error) {
    return "direct"
  }
}

function campaignValue(value) {
  const sanitized = value?.trim().slice(0, 100)
  return sanitized || undefined
}

function compact(object) {
  return Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined))
}

function eventProperties(element) {
  return compact(
    Object.fromEntries(
      EVENT_PROPERTIES.map((name) => [
        name.replace("analytics", "").replace(/^[A-Z]/, (letter) => letter.toLowerCase()),
        element.dataset[name],
      ])
    )
  )
}

function capture(event, properties = {}) {
  const config = configuration()
  const context = pageContext()
  if (!config || !context) return

  const payload = {
    api_key: config.token,
    event,
    distinct_id: sessionIdentifier(),
    properties: {
      $lib: "vaibe-web",
      $process_person_profile: false,
      ...acquisitionProperties(),
      ...context,
      ...properties,
    },
  }

  window.fetch(`${config.host}/i/v0/e/`, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => {})
}

function trackPageView() {
  if (!pageContext()) return

  capture("public_page_viewed")
}

function trackRenderedEvents(root = document) {
  root.querySelectorAll("[data-analytics-view-event]:not([data-analytics-captured])").forEach((element) => {
    element.dataset.analyticsCaptured = "true"
    capture(element.dataset.analyticsViewEvent, eventProperties(element))
  })
}

let visibilityObserver

function trackVisibleEvents(root = document) {
  if (!("IntersectionObserver" in window)) return

  visibilityObserver ||= new IntersectionObserver((entries) => {
    entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
      capture(entry.target.dataset.analyticsVisibilityEvent, eventProperties(entry.target))
      visibilityObserver.unobserve(entry.target)
    })
  }, { rootMargin: "-20% 0px -55% 0px", threshold: 0 })

  root.querySelectorAll("[data-analytics-visibility-event]:not([data-analytics-observed])").forEach((element) => {
    element.dataset.analyticsObserved = "true"
    visibilityObserver.observe(element)
  })
}

let lastPageViewKey

function pageViewKey(context) {
  return [ context?.page, context?.period, context?.topic ].filter(Boolean).join(":")
}

function trackFramePageView(frame) {
  const properties = eventProperties(frame)
  if (!properties.page) return

  const key = pageViewKey(properties)
  if (!key || key === lastPageViewKey) return

  capture("public_page_viewed", properties)
  lastPageViewKey = key
}

document.addEventListener("click", (event) => {
  const target = event.target.closest("[data-analytics-event]")
  if (!target) return

  capture(target.dataset.analyticsEvent, eventProperties(target))
})

document.addEventListener("submit", (event) => {
  const target = event.target.closest("[data-analytics-event]")
  if (!target) return

  capture(target.dataset.analyticsEvent, eventProperties(target))
})

document.addEventListener("vaibe:analytics", (event) => {
  const name = event.detail?.event
  if (!name) return

  capture(name, event.detail?.properties || {})
})

document.addEventListener("turbo:load", () => {
  trackPageView()
  lastPageViewKey = pageViewKey(pageContext())
  trackRenderedEvents()
  trackVisibleEvents()
})

document.addEventListener("turbo:frame-load", (event) => {
  trackFramePageView(event.target)
  trackRenderedEvents(event.target)
  trackVisibleEvents(event.target)
})
